import React, {FormEvent, ReactElement} from "react";
import {Form, Field, ObjectField, FeedbackStrategies} from "formula-one";
import {throttle} from "throttle-debounce";

import {IS_DEV_ENV} from "/src/config";
import {logErrorToSentry} from "/src/utils/sentry";
import {logEvent, updateUserProperties} from "/src/utils/amplitude";
import translate from "/src/utils/translate";
import {css, getQueryStringParams} from "/src/utils/utils";
import {getLastPaths, sessionStorageEnabled} from "/src/utils/path-logging";

import {injectFieldsIntoForm, submitFormData} from "/src/utils/forms";
import {AddressObjectType, SimpleFormProps} from "/src/types";
import {
  computeFormSubmitTargetURL,
  hasConditionalSteps,
} from "../form-conditions/multi-step-conditions/conditional-step-registry";
import {validateFormField} from "/src/utils/form-validator";

import Icon from "../../icon/Icon";
import Button from "../../buttons/button/Button";
import ErrorMessage from "../error-message/ErrorMessage";
import SimpleFormField from "../simple-form-field/SimpleFormField";
import GDPRContext, {GDPRContextState} from "/src/contexts/GDPRContext";
import MarkdownText, {
  MARKDOWN_TYPES,
} from "../../markdown/markdown-text/MarkdownText";

import FormSubmitSuccessIcon from "/src/svg/form-submit-success.svg";
import {getMsclkid} from "/src/utils/microsoft-click-id";
import useHasWindow from "/src/hooks/useHasWindow";

import * as styles from "./simple-form.module.scss";
import {containsEMEA} from "/src/utils/geolocate/geolocateUtils";

/**
 * session storage name to store partial form data
 *
 * if form embedded is true, on submit, we will store form data into session storage
 * session storage data will be used when subsequent form steps page is being loaded
 */
export const SESSION_STORAGE_NAME = "FlexportHISFormData";
const MAX_FORM_WIDTH = 444;
const DEFAULT_SUCCESS_PAGE =
  "https://www.flexport.com/thank-you/confirmation-page/";

const SimpleForm = (props: SimpleFormProps): ReactElement | null => {
  const {
    campaignId,
    classes,
    contentClasses,
    darkMode = false,
    devCampaignId,
    disableResize = false,
    disableSubmit = false,
    embedded = false,
    errorMessage,
    fieldItems = [],
    footnote,
    footnoteWithOrDivider,
    formId = "",
    isInvisible,
    isMultiStep = false,
    lastInquiryObject,
    layout = "full",
    name,
    onMount,
    onSubmitCallback,
    pardotFormHandlerUrl,
    postSubmissionUrl,
    showGDPR = true,
    submitButtonId = "",
    submitButtonLabel = "Submit",
    submittedSuccessfullyMessage,
    successMessageId = "",
    title = "",
    transparentBg = false,
    transparentTitle = false,
    updateConditionState,
    useLegacyPhoneInput = false,
  } = props;
  const gdpr = React.useContext<GDPRContextState>(GDPRContext);
  const {isEU, isEMEA} = gdpr;
  const [hasPardotFormHandlerError, setHasPardotFormHandlerError] =
    React.useState<boolean>(false);
  const [showFormError, setShowFormError] = React.useState<boolean>(false);
  const [busy, setBusy] = React.useState<boolean>(false);
  const throttleRef = React.useRef<any>(null);
  const mounted = React.useRef(false);
  const hasWindow = useHasWindow();
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  // const [initialFormValueState, setInitialFormValueState] =
  //   React.useState<Record<string, any> | null>(null);
  const formWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const isCompact: boolean = layout === "compact";
  const isDarkMode: boolean = darkMode === true;

  // we only load success message if
  // - post submission url is null
  // - submittedSuccessfullyMessage is not null and not empty
  const loadSuccessMessage: boolean =
    submittedSuccessfullyMessage != null &&
    submittedSuccessfullyMessage.trim() !== "" &&
    postSubmissionUrl == null;

  const initialFormValueState: Record<string, any> = {};

  const sessionStorageSupported = sessionStorageEnabled();

  const getFormDataFromSession = () => {
    const rawSessionFormData = sessionStorageSupported
      ? window.sessionStorage.getItem(SESSION_STORAGE_NAME)
      : null;
    const parsedSessionFormData =
      rawSessionFormData != null ? JSON.parse(rawSessionFormData) : {};

    return parsedSessionFormData;
  };

  const sessionFormData = getFormDataFromSession();
  // on mount let's set form initial state
  if (fieldItems && fieldItems.length > 0) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < fieldItems.length; i++) {
      if (fieldItems[i] != null) {
        const fieldItem:
          | Queries.FormFieldFragmentFragment
          | Queries.FormOptionsFragmentFragment = fieldItems[i];

        if (fieldItem.field_name != null) {
          // grab data from session storage, with the following fallback value
          let fieldItemValue = sessionFormData[fieldItem.field_name];
          if (fieldItemValue == null) {
            fieldItemValue = fieldItem.value || "";
          }
          // check if field item is checkbox
          const castedFieldItem =
            fieldItem as Queries.FormFieldFragmentFragment;
          if (castedFieldItem.type === "checkbox") {
            initialFormValueState[fieldItem.field_name] =
              castedFieldItem.checked === true ? fieldItemValue : "";
          } else {
            initialFormValueState[fieldItem.field_name] = fieldItemValue;
          }
        }
      }
    }
  }

  React.useEffect(() => {
    const params = getQueryStringParams();

    if (pardotFormHandlerUrl != null && params.get("errors") === "true") {
      setHasPardotFormHandlerError(true);
    }
  }, [pardotFormHandlerUrl]);

  const onSubmitClick = (valid: boolean) => {
    setShowFormError(!valid);
  };

  const handleFormSubmit = async (data: any) => {
    setBusy(true);
    // let's submit form data to pardot
    // let's prep the form data
    const formData = {
      // let's pass along the session data, in case there is no form element gets rendered
      ...getFormDataFromSession(),
      ...data,
      ...data.autocompleteAddress,
      campaignId: IS_DEV_ENV ? devCampaignId : campaignId,
      lastInquiryObject,
    };

    // Look for address fields by type so we can construct the fields properly for pardot
    // eslint-disable-next-line no-unused-expressions
    fieldItems &&
      fieldItems.forEach(item => {
        if (item && item.field_name && !pardotFormHandlerUrl) {
          // FMH-440:
          // Pardot v5 requires fields to match

          // Check if it's a MultiSelect and covert to Array
          if (
            item.__typename === "ContentfulFormOptions" &&
            item.options &&
            !item.dropdown &&
            !item.radio
          ) {
            // Get the string value from data.
            const stringValue = data[item.field_name];

            // Convert to array (if it's a string), or leave it as is (if somehow already an array).
            const arrayValue =
              typeof stringValue === "string"
                ? stringValue.split(",").filter(part => part)
                : Array.isArray(stringValue)
                ? stringValue
                : []; // if not string or array default to empty array

            // OVERWRITE the value in formData with the array.
            formData[item.field_name] = arrayValue;
          }

          // Check if it's Number and convert to float
          if (
            item.__typename === "ContentfulFormField" &&
            item.type === "number"
          ) {
            const stringValue = data[item.field_name];
            // OVERWRITE the value in formData with a float.
            formData[item.field_name] = parseFloat(stringValue);
          }
        }
        if (
          item != null &&
          ((item as Queries.FormFieldFragmentFragment).type ===
            "mailingPhysicalAddress" ||
            (item as Queries.FormFieldFragmentFragment).type === "address")
        ) {
          const addressObject = formData[item.field_name!];

          formData.address_one = addressObject.address_one;
          formData.address_two = addressObject.address_two;
          formData.city = addressObject.city;
          formData.country = addressObject.country;
          formData.state = addressObject.state;
          formData.zip = addressObject.zip;

          if (
            (item as Queries.FormFieldFragmentFragment).type ===
            "mailingPhysicalAddress"
          ) {
            formData.Physical_Street_Address =
              addressObject.physical_street_address;
            formData.Physical_Street_Address_2 =
              addressObject.physical_street_address2;
            formData.Physical_City = addressObject.physical_city;
            formData.Physical_Country = addressObject.physical_country;
            formData.Physical_State = addressObject.physical_state;
            formData.Physical_Postal_Code = addressObject.physical_postal_code;
          }

          // address data comes in as an object,
          // which we merged into our formData object in the step above, bringing each key value address pair up one level,
          // and now just need to delete this original object from formData
          delete formData[item.field_name!];
        }
      });

    // Check if this form should be sent to Pardot directly (pardotFormHandlerUrl)
    if (pardotFormHandlerUrl != null && formRef.current != null) {
      injectFieldsIntoForm(formData, formRef.current);

      // Submit the form to Pardot
      if (formRef.current != null) {
        formRef.current.submit();
      }

      return;
    }

    // We only want to log the form complete event to Amplitude if this is final step and if there isn't a follow up form
    if (submitButtonLabel === "Submit" || submitButtonLabel === "Finish") {
      // pad the array with empty strings
      const paths = ["", "", ...getLastPaths(2)].slice(-2);

      // this is here because of eslint's prefer-destructing rule
      const path1 = paths[0];
      const path2 = paths[1];

      formData.Flexport_User_Journey_Page_1 = path1;
      formData.Flexport_User_Journey_Page_2 = path2;

      logEvent("load:signup_flow_complete");
    } else if (submitButtonLabel === "Continue") {
      logEvent("load:partial_form_complete", {
        formTitle: title || "",
      });
    } else if (submitButtonLabel === "Subscribe") {
      logEvent("load:subscription", {
        formTitle: title || "",
      });
      updateUserProperties({isMarketableLead: "true"});
    } else if (submitButtonLabel === "Anmelden") {
      logEvent("load:subscription", {
        formTitle: title || "",
      });
      updateUserProperties({isMarketableLead: "true"});
    }
    // destroy session data on each submit, so that it won't get carried
    // forward by mistake
    if (sessionStorageSupported) {
      window.sessionStorage.removeItem(SESSION_STORAGE_NAME);
    }

    // compute final post submission url based on multi-step form conditions(if any)
    let finalSubmitUrl = postSubmissionUrl;
    let conditionUrl = null;
    const hasConditions = hasConditionalSteps(name || "");

    if (hasConditions) {
      conditionUrl = computeFormSubmitTargetURL(formData, name || "");
      finalSubmitUrl = conditionUrl || postSubmissionUrl;
    }

    try {
      if (disableSubmit === false) {
        await submitFormData(formData, gdpr);
      }

      if (onSubmitCallback) {
        await onSubmitCallback(formData, undefined, conditionUrl);
      }

      // if embedded is true, let's store partial form data into session storage
      if (embedded === true && sessionStorageSupported) {
        window.sessionStorage.setItem(
          SESSION_STORAGE_NAME,
          JSON.stringify(formData)
        );
      }

      setSubmitted(true);
    } catch (err: any) {
      if (onSubmitCallback) {
        await onSubmitCallback(formData, err, finalSubmitUrl);
      }
      logErrorToSentry(err);
      setShowFormError(true);
      setSubmitted(false);
    } finally {
      setBusy(false);
    }

    if (disableSubmit === false && finalSubmitUrl != null) {
      if (embedded === true) {
        window.open(finalSubmitUrl);
        return;
      }

      window.location.assign(finalSubmitUrl);
    }
  };

  const throttleFunc = throttle(
    1000,
    data => {
      // GROW-3388: we need to add the hidden "country_code" field since
      // formula-one doesn't pick up hidden fields
      const formData = formRef.current
        ? new FormData(formRef.current)
        : new Map();
      const countryCode = formData.get("country_code");
      const finalData =
        countryCode == null ? data : {...data, country_code: countryCode};

      handleFormSubmit(finalData);
    },
    {noTrailing: true}
  );

  // we watch on resize event, to compute wrapper min height,
  // we don't need to set min height if it's in mobile view
  // this is to avoid jumpyness in toggling submit success state
  const onResize = () => {
    if (formRef.current && formWrapperRef.current) {
      const isDesktop = window.innerWidth > 1024;

      formWrapperRef.current.style.minWidth =
        isDesktop &&
        formRef.current.offsetWidth > 0 &&
        formRef.current.offsetWidth <= MAX_FORM_WIDTH
          ? `${formRef.current.offsetWidth}px`
          : "auto";

      formWrapperRef.current.style.minHeight = isDesktop
        ? `${formRef.current.offsetHeight}px`
        : "auto";
    }
  };

  // on mount
  React.useEffect(() => {
    // we only care about the jumpy issue when we need to render
    // form submit success message
    if (disableResize === false && loadSuccessMessage) {
      throttleRef.current = throttle(300, onResize);

      // wait for form to be completely loaded
      requestAnimationFrame(() => {
        if (
          mounted.current === false &&
          formRef.current !== null &&
          formRef.current.offsetHeight > 0
        ) {
          onResize();
          onMount && onMount();
          mounted.current = true;
        }
      });

      window.addEventListener("resize", throttleRef.current);
    }

    return () => {
      if (throttleRef.current) {
        window.removeEventListener("resize", throttleRef.current);
      }
    };
  });

  if (!hasWindow) {
    return null;
  }

  return !initialFormValueState ? null : (
    <div
      ref={formWrapperRef}
      className={css(
        styles.simpleForm,
        isMultiStep ? styles.simpleFormMulti : styles.simpleFormSimple,
        darkMode ? styles.simpleFormDark : "",
        transparentBg ? styles.simpleFormTransparent : "",
        submitted ? styles.simpleFormSubmitted : "",
        isCompact ? styles.simpleFormCompact : "",
        transparentTitle ? styles.simpleFormTransparentTitle : "",
        classes!
      )}
    >
      {title != null && (
        <div
          id={formId || ""}
          className={css(
            "simple-form__title-wrapper",
            styles.simpleForm__titleWrapper
          )}
        >
          {/* added "simple-form__heading" so that parents can easily override styles */}
          <h2
            className={css(
              "h3",
              "simple-form__heading",
              styles.simpleForm__heading
            )}
          >
            {title}
          </h2>
        </div>
      )}
      {loadSuccessMessage && (
        <div
          role={submitted ? "alert" : "none"}
          aria-hidden={!submitted}
          className={css(
            "simple-form__msg-wrapper",
            styles.simpleForm__msgWrapper,
            contentClasses!
          )}
          id={successMessageId || ""}
        >
          <Icon
            svg={FormSubmitSuccessIcon}
            role="img"
            classes={styles.simpleForm__icon}
          />
          <div className={css("h4", styles.simpleForm__successMsg)}>
            {submittedSuccessfullyMessage}
          </div>
        </div>
      )}
      <Form
        initialValue={initialFormValueState}
        feedbackStrategy={FeedbackStrategies.Submitted}
        onSubmit={(data, extraData, tips) => {
          if (tips.valid.client === true) {
            throttleFunc(data);
          }
        }}
      >
        {(link, onSubmit, {valid}) => (
          <ObjectField link={link}>
            {links => (
              <form
                ref={formRef}
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                }}
                aria-hidden={loadSuccessMessage && submitted}
                className={css(
                  styles.simpleForm__form,
                  loadSuccessMessage && submitted
                    ? styles.simpleForm__formHidden
                    : ""
                )}
                name={name!}
                action={pardotFormHandlerUrl!}
                encType="application/x-www-form-urlencoded"
                method="POST"
              >
                <div
                  className={css(
                    "simple-form__form-content",
                    styles.simpleForm__formContent,
                    contentClasses!
                  )}
                >
                  {pardotFormHandlerUrl != null && (
                    <>
                      <input
                        type="hidden"
                        name="success_location"
                        value={
                          postSubmissionUrl != null
                            ? postSubmissionUrl
                            : DEFAULT_SUCCESS_PAGE
                        }
                      />
                      <input
                        type="hidden"
                        name="error_location"
                        value={`${window.location.origin}${window.location.pathname}`}
                      />
                    </>
                  )}

                  {fieldItems && fieldItems.length > 0 && (
                    <div className={css(styles.simpleForm__formFields)}>
                      {isCompact ? (
                        <>
                          <div
                            className={css(
                              styles.simpleForm__formFieldsCompact
                            )}
                          >
                            {fieldItems[0] &&
                              fieldItems[0].field_name != null && (
                                <Field
                                  link={links[fieldItems[0].field_name]}
                                  validation={(
                                    val: string | AddressObjectType
                                  ) => {
                                    const invisible =
                                      (isInvisible &&
                                        isInvisible(
                                          fieldItems[0]?.field_name || ""
                                        )) ||
                                      false;

                                    const castedField =
                                      fieldItems[0] as Queries.FormFieldFragmentFragment;
                                    return validateFormField(
                                      castedField?.type || "",
                                      castedField?.required === true,
                                      invisible ||
                                        (!isEMEA &&
                                          containsEMEA(
                                            fieldItems[0]
                                              .onlyShowForRegion as []
                                          )),
                                      castedField?.field_name!,
                                      val
                                    );
                                  }}
                                >
                                  {/* we only grab first field item */}
                                  {(value, errors, onChange: any) => (
                                    <div
                                      className={css(
                                        styles.simpleForm__fieldWrapper,
                                        (
                                          fieldItems[0] as Queries.FormFieldFragmentFragment
                                        )?.isFullWidth === false
                                          ? styles.simpleForm__fieldWrapperHalf
                                          : ""
                                      )}
                                    >
                                      {fieldItems[0] && (
                                        <SimpleFormField
                                          field={fieldItems[0]}
                                          status={
                                            errors.length > 0 ? "error" : ""
                                          }
                                          darkMode={isDarkMode}
                                          disabled={false}
                                          readOnly={false}
                                          onChange={onChange}
                                          value={value.toString()}
                                          valueForAddressField={
                                            typeof value === "object"
                                              ? value
                                              : undefined
                                          }
                                          transparentBg={transparentBg}
                                          isEU={isEU}
                                          isEMEA={isEMEA}
                                          updateConditionState={
                                            updateConditionState
                                          }
                                          isInvisible={isInvisible!}
                                          useLegacyPhoneInput={
                                            useLegacyPhoneInput
                                          }
                                        />
                                      )}
                                    </div>
                                  )}
                                </Field>
                              )}
                            <div
                              className={css(
                                styles.simpleForm__btnWrapper,
                                styles.simpleForm__btnWrapperCompact
                              )}
                            >
                              <Button
                                onClick={(extraData: any) => {
                                  onSubmitClick(valid);
                                  onSubmit(extraData);
                                }}
                                classes={styles.simpleForm__btn}
                                kind="primary"
                                type="submit"
                                loading={busy}
                                id={submitButtonId}
                              >
                                {translate(submitButtonLabel || "")}
                              </Button>
                            </div>
                          </div>
                          {/* this is to grab any additional form elem, i.e. checkbox */}
                          {fieldItems.length > 1 &&
                            fieldItems.map((fieldItem: any, i) => {
                              // we only care about second items onwards, as the first item already
                              // rendered above
                              if (
                                fieldItem &&
                                fieldItem.field_name != null &&
                                i > 0
                              ) {
                                return (
                                  <Field
                                    key={`field-${fieldItem.field_name}`}
                                    link={links[fieldItem.field_name]}
                                    validation={(
                                      val: string | AddressObjectType
                                    ) => {
                                      const invisible =
                                        (isInvisible &&
                                          isInvisible(
                                            fieldItem.field_name || ""
                                          )) ||
                                        false;
                                      return validateFormField(
                                        fieldItem?.type || "",
                                        fieldItem?.required === true,
                                        invisible ||
                                          (!isEMEA &&
                                            containsEMEA(
                                              fieldItem.onlyShowForRegion as []
                                            )),
                                        fieldItem?.field_name,
                                        val
                                      );
                                    }}
                                  >
                                    {(value, errors, onChange) => (
                                      <div
                                        className={css(
                                          styles.simpleForm__fieldWrapper,
                                          fieldItem.isFullWidth === false
                                            ? styles.simpleForm__fieldWrapperHalf
                                            : ""
                                        )}
                                      >
                                        <SimpleFormField
                                          field={fieldItem}
                                          status={
                                            errors.length > 0 ? "error" : ""
                                          }
                                          darkMode={isDarkMode}
                                          disabled={false}
                                          readOnly={false}
                                          onChange={onChange as any}
                                          value={value.toString()}
                                          valueForAddressField={
                                            typeof value === "object"
                                              ? value
                                              : undefined
                                          }
                                          transparentBg={transparentBg}
                                          isEU={isEU}
                                          isEMEA={isEMEA}
                                          updateConditionState={
                                            updateConditionState
                                          }
                                          isInvisible={isInvisible!}
                                          useLegacyPhoneInput={
                                            useLegacyPhoneInput
                                          }
                                        />
                                      </div>
                                    )}
                                  </Field>
                                );
                              }

                              return null;
                            })}
                          <div
                            className={css(
                              styles.simpleForm__btnWrapper,
                              styles.simpleForm__btnWrapperCompactMobile
                            )}
                          >
                            <Button
                              onClick={(extraData: any) => {
                                onSubmitClick(valid);
                                onSubmit(extraData);
                              }}
                              classes={styles.simpleForm__btn}
                              kind="primary"
                              type="submit"
                              loading={busy}
                              id={submitButtonId}
                            >
                              {translate(submitButtonLabel || "")}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {fieldItems.map((fieldItem: any) => {
                            return fieldItem && fieldItem.field_name != null ? (
                              <Field
                                key={`field-${fieldItem.field_name}`}
                                link={links[fieldItem.field_name]}
                                validation={(
                                  val: string | AddressObjectType
                                ) => {
                                  const invisible =
                                    (isInvisible &&
                                      isInvisible(
                                        fieldItem.field_name || ""
                                      )) ||
                                    false;
                                  return validateFormField(
                                    fieldItem?.type || "",
                                    fieldItem?.required === true,
                                    invisible ||
                                      (!isEMEA &&
                                        containsEMEA(
                                          fieldItem.onlyShowForRegion as []
                                        )),
                                    fieldItem?.field_name,
                                    val
                                  );
                                }}
                              >
                                {(value, errors, onChange) => (
                                  <div
                                    className={css(
                                      styles.simpleForm__fieldWrapper,
                                      fieldItem.isFullWidth === false
                                        ? styles.simpleForm__fieldWrapperHalf
                                        : ""
                                    )}
                                  >
                                    <SimpleFormField
                                      field={fieldItem}
                                      status={errors.length > 0 ? "error" : ""}
                                      darkMode={isDarkMode}
                                      disabled={false}
                                      readOnly={false}
                                      onChange={onChange as any}
                                      value={value.toString()}
                                      valueForAddressField={
                                        typeof value === "object"
                                          ? value
                                          : undefined
                                      }
                                      transparentBg={transparentBg}
                                      isEU={isEU}
                                      isEMEA={isEMEA}
                                      updateConditionState={
                                        updateConditionState
                                      }
                                      isInvisible={isInvisible}
                                      useLegacyPhoneInput={useLegacyPhoneInput}
                                    />
                                  </div>
                                )}
                              </Field>
                            ) : null;
                          })}
                        </>
                      )}
                    </div>
                  )}

                  {isEU && showGDPR === true && (
                    <div className={css(styles.simpleForm__gdpr)}>
                      <span
                        className={css(
                          "p-small",
                          styles.simpleForm__gdpr__content
                        )}
                      >
                        {translate(
                          "By submitting this form, you agree that we may contact you using the information you provided in this form about our products and services. Your data will be used subject to Flexport’s Privacy Policy. You can unsubscribe at any time using the link in our emails."
                        )}
                      </span>
                    </div>
                  )}

                  {footnote != null && (
                    <div
                      className={css("p-small", styles.simpleForm__footnote)}
                    >
                      <span>{footnote}</span>
                    </div>
                  )}
                  {getMsclkid() !== null && (
                    <input
                      type="hidden"
                      value={getMsclkid()}
                      name="msclkid"
                      id="msclkid"
                    />
                  )}
                  {!isCompact && (
                    <div className={css(styles.simpleForm__btnWrapper)}>
                      <Button
                        onClick={(extraData: any) => {
                          onSubmitClick(valid);
                          onSubmit(extraData);
                        }}
                        classes={css(
                          styles.simpleForm__btn,
                          styles.simpleForm__btnFull
                        )}
                        kind="primary"
                        type="submit"
                        loading={busy}
                        id={submitButtonId}
                      >
                        {translate(submitButtonLabel || "")}
                      </Button>
                    </div>
                  )}
                  {showFormError && (
                    <ErrorMessage
                      message={errorMessage || ""}
                      visible={showFormError}
                    />
                  )}

                  {hasPardotFormHandlerError && (
                    <ErrorMessage
                      message={translate(
                        "We were unable to successfully submit your data. Please check that required fields have been filled and have the appropriate information."
                      )}
                      visible={hasPardotFormHandlerError}
                    />
                  )}

                  {footnoteWithOrDivider != null &&
                    footnoteWithOrDivider.footnoteWithOrDivider != null &&
                    footnoteWithOrDivider.footnoteWithOrDivider.trim() !==
                      "" && (
                      <div
                        className={css(
                          styles.simpleForm__footnoteWithOrDivider
                        )}
                      >
                        <div
                          className={css(
                            "p-small",
                            styles.simpleForm__footnoteWithOrDivider__divider
                          )}
                        >
                          {translate("OR")}
                        </div>

                        <MarkdownText
                          type={MARKDOWN_TYPES.ARTICLE}
                          text={footnoteWithOrDivider.footnoteWithOrDivider}
                        />
                      </div>
                    )}
                </div>
              </form>
            )}
          </ObjectField>
        )}
      </Form>
    </div>
  );
};

export default SimpleForm;
